'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

// ─── GARDEN WORKSHOP — ACTIVE CANVAS ─────────────────────────────────────────
// Caretakers assemble their own garden launchpads by selecting platform components
// and composing them into a personal garden page that is publicly accessible.

interface ModuleOption {
  id: string;
  type: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  color: string;
  tags: string[];
}

const AVAILABLE_MODULES: ModuleOption[] = [
  { id: 'irl-lens', type: 'irl_lens', title: 'IRL Lens', description: 'Inner Reality Lens — 8-layer recognition pathway for reading Names in creation', href: '/inner-reality-lens', icon: '🔬', color: 'rgba(45,122,71,0.8)', tags: ['observation', 'names', 'creation'] },
  { id: 'mrg', type: 'mrg', title: 'Muslim Resource Guide', description: 'Community resource mapping across 9 cities and 72 wards', href: '/muslim-resource-guide', icon: '🗺', color: 'rgba(45,138,135,0.8)', tags: ['community', 'mapping', 'resources'] },
  { id: 'names-reference', type: 'names_tile', title: 'Names Reference', description: '99+ Names of Allah with root analyses and meaning fields', href: '/names-reference', icon: '✦', color: 'rgba(201,168,76,0.8)', tags: ['names', 'roots', 'theology'] },
  { id: 'keys', type: 'keys_card', title: 'Keys to Kulli Haal', description: 'The 7-key curriculum: Ayat, Haal, Taqwah, Mizan, Amanah, Haqq, Islah', href: '/keys-to-kulli-haal', icon: '🗝', color: 'rgba(139,105,20,0.8)', tags: ['curriculum', 'keys', 'framework'] },
  { id: 'open-letter', type: 'letter_section', title: 'Open Letter', description: 'The foundational letter — any section can be planted as a seed', href: '/open-letter', icon: '📜', color: 'rgba(212,197,169,0.6)', tags: ['letter', 'foundation', 'text'] },
  { id: 'ayat-in-creation', type: 'concept_term', title: 'Ayat in Creation', description: 'Signs in the horizons and in yourselves — dual Ayat framework', href: '/ayat-in-creation', icon: '🌿', color: 'rgba(45,122,71,0.6)', tags: ['ayat', 'creation', 'signs'] },
  { id: 'sl-glossary', type: 'concept_term', title: 'SL Glossary', description: '20+ terms with etymology, root analyses, and meaning fields', href: '/sl-glossary', icon: '📖', color: 'rgba(138,138,184,0.7)', tags: ['glossary', 'terms', 'roots'] },
  { id: 'quests', type: 'quest', title: 'Quests', description: 'Uboor Bridge-Quests — paths upon which knowledge is sought', href: '/quests', icon: '⬡', color: 'rgba(45,138,135,0.6)', tags: ['quests', 'learning', 'paths'] },
  { id: 'community', type: 'concept_term', title: 'Community', description: 'Nations and tribes that you may know one another — Quran 49:13', href: '/community', icon: '🌐', color: 'rgba(45,122,71,0.5)', tags: ['community', 'ummah', 'connection'] },
  { id: 'bridge-env', type: 'concept_term', title: 'Bridge: Environment Design', description: 'The designed environment as a reading of the Qadr', href: '/bridge-environment-design', icon: '🌉', color: 'rgba(201,168,76,0.5)', tags: ['bridge', 'environment', 'design'] },
];

interface PlantedModule {
  id: string;
  moduleId: string;
  title: string;
  description: string;
  type: string;
  href: string;
  icon: string;
  color: string;
  position: number;
  note?: string;
}

interface GardenComposition {
  id?: string;
  title: string;
  description: string;
  slug?: string;
  is_public: boolean;
  lineage: string;
  vocation: string;
  modules: PlantedModule[];
}

export default function GardenWorkshop() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'browse' | 'compose' | 'preview'>('browse');
  const [garden, setGarden] = useState<GardenComposition>({
    title: 'My Garden',
    description: '',
    is_public: true,
    lineage: '',
    vocation: '',
    modules: [],
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedSlug, setSavedSlug] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [noteTarget, setNoteTarget] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  const filtered = AVAILABLE_MODULES.filter(m =>
    !search || m.title.toLowerCase().includes(search.toLowerCase()) ||
    m.tags.some(t => t.includes(search.toLowerCase()))
  );

  const plantModule = useCallback((mod: ModuleOption) => {
    if (garden.modules.find(m => m.moduleId === mod.id)) return;
    const planted: PlantedModule = {
      id: `planted-${Date.now()}`,
      moduleId: mod.id,
      title: mod.title,
      description: mod.description,
      type: mod.type,
      href: mod.href,
      icon: mod.icon,
      color: mod.color,
      position: garden.modules.length,
    };
    setGarden(g => ({ ...g, modules: [...g.modules, planted] }));
    setTab('compose');
  }, [garden.modules]);

  const removeModule = useCallback((id: string) => {
    setGarden(g => ({ ...g, modules: g.modules.filter(m => m.id !== id) }));
  }, []);

  const moveModule = useCallback((id: string, dir: -1 | 1) => {
    setGarden(g => {
      const idx = g.modules.findIndex(m => m.id === id);
      if (idx < 0) return g;
      const newMods = [...g.modules];
      const target = idx + dir;
      if (target < 0 || target >= newMods.length) return g;
      [newMods[idx], newMods[target]] = [newMods[target], newMods[idx]];
      return { ...g, modules: newMods };
    });
  }, []);

  const saveGarden = useCallback(async () => {
    setSaving(true);
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }

    const slug = garden.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-' + user.id.slice(0, 8);

    const { data: comp, error } = await supabase
      .from('garden_compositions')
      .upsert({
        user_id: user.id,
        title: garden.title,
        description: garden.description,
        is_public: garden.is_public,
        slug,
        lineage: garden.lineage,
        vocation: garden.vocation,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'slug' })
      .select()
      .single();

    if (comp && !error) {
      // Save modules
      await supabase.from('garden_modules').delete().eq('composition_id', comp.id);
      if (garden.modules.length > 0) {
        await supabase.from('garden_modules').insert(
          garden.modules.map((m, i) => ({
            composition_id: comp.id,
            user_id: user.id,
            module_type: m.type,
            module_ref: m.moduleId,
            title: m.title,
            description: m.note || m.description,
            position: i,
            embed_enabled: true,
          }))
        );
      }
      setSavedSlug(slug);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  }, [garden]);

  return (
    <div style={{ background: 'rgba(6,13,18,0.98)', borderTop: '1px solid rgba(45,122,71,0.12)', padding: '40px 0' }}>
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        {/* Header */}
        <button
          onClick={() => setOpen(!open)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', background: 'rgba(45,122,71,0.04)',
            border: '1px solid rgba(45,122,71,0.2)', borderRadius: 8,
            padding: '14px 20px', cursor: 'pointer',
          }}
          aria-expanded={open}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(45,200,100,0.7)' }}>
              🌱 Garden Workshop
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)' }}>
              assemble · plant · transplant · share
            </span>
            {garden.modules.length > 0 && (
              <span style={{ fontFamily: 'monospace', fontSize: 9, background: 'rgba(45,122,71,0.12)', border: '1px solid rgba(45,122,71,0.3)', borderRadius: 4, padding: '2px 8px', color: 'rgba(45,200,100,0.8)' }}>
                {garden.modules.length} planted
              </span>
            )}
          </div>
          <span style={{ color: 'rgba(45,200,100,0.4)', fontSize: 11, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
        </button>

        {open && (
          <div style={{ marginTop: 16, animation: 'gwFadeIn 0.22s ease' }}>
            <style>{`@keyframes gwFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>

            {/* Garden metadata */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <input
                value={garden.title}
                onChange={e => setGarden(g => ({ ...g, title: e.target.value }))}
                placeholder="Garden title…"
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 6, padding: '8px 12px', color: 'rgba(212,197,169,0.8)',
                  fontFamily: 'monospace', fontSize: 12, outline: 'none',
                }}
              />
              <input
                value={garden.vocation}
                onChange={e => setGarden(g => ({ ...g, vocation: e.target.value }))}
                placeholder="Your vocation / role…"
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 6, padding: '8px 12px', color: 'rgba(212,197,169,0.8)',
                  fontFamily: 'monospace', fontSize: 12, outline: 'none',
                }}
              />
              <textarea
                value={garden.description}
                onChange={e => setGarden(g => ({ ...g, description: e.target.value }))}
                placeholder="Garden description — what is this launchpad for?"
                rows={2}
                style={{
                  gridColumn: '1 / -1', background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
                  padding: '8px 12px', color: 'rgba(212,197,169,0.7)',
                  fontFamily: 'monospace', fontSize: 11, outline: 'none', resize: 'vertical',
                }}
              />
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              {(['browse', 'compose', 'preview'] as const).map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  padding: '5px 14px', border: `1px solid ${tab === t ? 'rgba(45,200,100,0.4)' : 'rgba(255,255,255,0.07)'}`,
                  background: tab === t ? 'rgba(45,122,71,0.1)' : 'transparent',
                  color: tab === t ? 'rgba(45,200,100,0.9)' : 'rgba(212,197,169,0.4)',
                  borderRadius: 4, cursor: 'pointer',
                }}>
                  {t === 'browse' ? '⬡ Browse Modules' : t === 'compose' ? `🌱 Compose (${garden.modules.length})` : '◈ Preview'}
                </button>
              ))}
            </div>

            {/* Browse tab */}
            {tab === 'browse' && (
              <div>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search modules…"
                  style={{
                    width: '100%', background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6,
                    padding: '8px 12px', color: 'rgba(212,197,169,0.7)',
                    fontFamily: 'monospace', fontSize: 11, outline: 'none', marginBottom: 12,
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
                  {filtered.map(mod => {
                    const planted = !!garden.modules.find(m => m.moduleId === mod.id);
                    return (
                      <div key={mod.id} style={{
                        background: planted ? 'rgba(45,122,71,0.06)' : 'rgba(255,255,255,0.02)',
                        border: `1px solid ${planted ? 'rgba(45,122,71,0.3)' : 'rgba(255,255,255,0.06)'}`,
                        borderRadius: 8, padding: '12px 14px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 16 }}>{mod.icon}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.85)', fontWeight: 700 }}>{mod.title}</span>
                        </div>
                        <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.4)', lineHeight: 1.6, marginBottom: 10 }}>
                          {mod.description}
                        </p>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => plantModule(mod)}
                            disabled={planted}
                            style={{
                              fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                              padding: '4px 10px', border: `1px solid ${planted ? 'rgba(45,122,71,0.4)' : 'rgba(45,200,100,0.3)'}`,
                              background: planted ? 'rgba(45,122,71,0.1)' : 'rgba(45,122,71,0.06)',
                              color: planted ? 'rgba(45,200,100,0.6)' : 'rgba(45,200,100,0.8)',
                              borderRadius: 4, cursor: planted ? 'default' : 'pointer', flex: 1,
                            }}
                          >
                            {planted ? '✓ Planted' : '🌱 Plant'}
                          </button>
                          <Link href={mod.href} style={{
                            fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase',
                            padding: '4px 10px', border: '1px solid rgba(255,255,255,0.07)',
                            background: 'transparent', color: 'rgba(212,197,169,0.4)',
                            borderRadius: 4, textDecoration: 'none', display: 'flex', alignItems: 'center',
                          }}>
                            ↗
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Compose tab */}
            {tab === 'compose' && (
              <div>
                {garden.modules.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(212,197,169,0.3)', fontFamily: 'monospace', fontSize: 12 }}>
                    No modules planted yet. Browse and plant modules to compose your garden.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {garden.modules.map((mod, idx) => (
                      <div key={mod.id} style={{
                        background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: 8, padding: '10px 14px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.2)', width: 16 }}>{idx + 1}</span>
                          <span style={{ fontSize: 14 }}>{mod.icon}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.8)', flex: 1 }}>{mod.title}</span>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => moveModule(mod.id, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', color: 'rgba(212,197,169,0.3)', cursor: idx === 0 ? 'default' : 'pointer', fontSize: 12 }}>↑</button>
                            <button onClick={() => moveModule(mod.id, 1)} disabled={idx === garden.modules.length - 1} style={{ background: 'none', border: 'none', color: 'rgba(212,197,169,0.3)', cursor: idx === garden.modules.length - 1 ? 'default' : 'pointer', fontSize: 12 }}>↓</button>
                            <button onClick={() => setNoteTarget(noteTarget === mod.id ? null : mod.id)} style={{ background: 'none', border: 'none', color: 'rgba(201,168,76,0.4)', cursor: 'pointer', fontSize: 10, fontFamily: 'monospace' }}>note</button>
                            <button onClick={() => removeModule(mod.id)} style={{ background: 'none', border: 'none', color: 'rgba(200,80,80,0.4)', cursor: 'pointer', fontSize: 12 }}>✕</button>
                          </div>
                        </div>
                        {noteTarget === mod.id && (
                          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                            <input
                              value={mod.note || ''}
                              onChange={e => setGarden(g => ({ ...g, modules: g.modules.map(m => m.id === mod.id ? { ...m, note: e.target.value } : m) }))}
                              placeholder="Add a note about why you planted this…"
                              style={{
                                width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                                borderRadius: 4, padding: '6px 10px', color: 'rgba(212,197,169,0.6)',
                                fontFamily: 'monospace', fontSize: 10, outline: 'none', boxSizing: 'border-box',
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Preview tab */}
            {tab === 'preview' && (
              <div style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '20px 24px' }}>
                <h3 style={{ fontFamily: 'monospace', fontSize: 16, color: '#F5F0E8', marginBottom: 4 }}>{garden.title || 'My Garden'}</h3>
                {garden.vocation && <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(201,168,76,0.6)', marginBottom: 8 }}>Vocation: {garden.vocation}</p>}
                {garden.description && <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(212,197,169,0.5)', lineHeight: 1.6, marginBottom: 16 }}>{garden.description}</p>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                  {garden.modules.map(mod => (
                    <div key={mod.id} style={{
                      background: 'rgba(255,255,255,0.02)', border: `1px solid ${mod.color}33`,
                      borderRadius: 8, padding: '12px', textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 20, marginBottom: 6 }}>{mod.icon}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.7)', marginBottom: 4 }}>{mod.title}</div>
                      {mod.note && <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.35)', fontStyle: 'italic' }}>{mod.note}</div>}
                    </div>
                  ))}
                </div>
                {savedSlug && (
                  <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 6 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(45,200,100,0.7)', marginBottom: 4 }}>✓ Garden published</p>
                    <Link href={`/garden/${savedSlug}`} style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(45,138,135,0.8)', textDecoration: 'none' }}>
                      /garden/{savedSlug} ↗
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Save bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={garden.is_public}
                  onChange={e => setGarden(g => ({ ...g, is_public: e.target.checked }))}
                  style={{ accentColor: '#2d7a47' }}
                />
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.5)' }}>
                  Public garden — visible to all caretakers
                </span>
              </label>
              <button
                onClick={saveGarden}
                disabled={saving || garden.modules.length === 0}
                style={{
                  fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  padding: '7px 18px', border: `1px solid ${saved ? 'rgba(45,122,71,0.5)' : 'rgba(45,200,100,0.3)'}`,
                  background: saved ? 'rgba(45,122,71,0.12)' : 'rgba(45,122,71,0.08)',
                  color: saved ? 'rgba(45,200,100,0.9)' : 'rgba(45,200,100,0.7)',
                  borderRadius: 6, cursor: saving || garden.modules.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: garden.modules.length === 0 ? 0.4 : 1,
                }}
              >
                {saving ? '…saving' : saved ? '✓ Garden Saved' : '🌱 Publish Garden'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
